import React, { useMemo, useState } from 'react';
import { Modal, Tooltip } from 'antd';
import moment from 'moment';
import {
  AppWindow,
  Eye,
  FileText,
  Globe,
  Image as ImageIcon,
  MousePointerClick,
  Activity,
  Clock,
  Files,
  Server,
  Smartphone,
  Tablet,
  Monitor,
  ArrowRight,
} from 'lucide-react';

const APP_PLATFORM_ICONS = {
  Application: AppWindow,
  Website: Globe,
  'Ad Server': Server,
};

import { getMobileOperatingSystem, determineAdServer } from './sessionMacroResolution';

// =============================================================================
// SessionJourneyExpand (V1 Sessions) — redesigned to a unified merged
// chronological timeline. Replaces the previous two stacked timelines
// (Impression Journey + Session Journey) which told the same story in two
// parallel columns. A user's session is one ordered sequence — show it
// that way.
//
// Layout:
//   1. Summary strip — at-a-glance metrics (Pages · Duration · Device ·
//      Flow). Mirror compact-card pattern (PostHog / Hotjar / Plausible).
//   2. Unified timeline — events from `impressions[]` + `journey[]`
//      merged + sorted by timestamp ascending. Each row carries:
//        · left time gutter (mono, tabular-nums)
//        · type pill (Impression / Page View / Click / etc.) with color
//        · content (publisher block for impressions; URL + event tag for
//          journey events)
//
// Macro-resolution rules from V1 (TTD/CENTRO/LiquidM ad-server detection,
// app-store/sitename fallback chains, OS classification) are preserved
// verbatim in `sessionMacroResolution.js` and applied here as a
// transformation step. Only the visual chrome moves.
// =============================================================================

const safeDecode = (s) => {
  try {
    return decodeURIComponent(s);
  } catch (e) {
    return s;
  }
};

const secondsToTimeText = (seconds) => {
  if (seconds < 0 || !Number.isFinite(seconds)) return '—';
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  const parts = [];
  if (hrs > 0) parts.push(`${hrs}h`);
  if (mins > 0) parts.push(`${mins}m`);
  if (secs > 0 || parts.length === 0) parts.push(`${secs}s`);
  return parts.join(' ');
};

// ---------------------------------------------------------------------------
// Impression normalizer — same fallback ladder as the legacy makeTimeLine
// mapper, distilled to the fields we render.
// ---------------------------------------------------------------------------
const normalizeImpression = (impression, i) => {
  const {
    IMPRESSION_TIME,
    IMPRESSION_USERAGENT,
    PAGE_URLHOST,
    IMPRESSION_REFERRER,
    IMPRESSION_PUBLISHERNAME,
    IMPRESSION_APPNAME,
    IMPRESSION_APPSTORE_ID,
    IMPRESSION_SITE_ID,
    IMPRESSION_SITENAME,
    CREATIVEURL,
    CREATIVEID,
  } = impression;

  const OS = IMPRESSION_USERAGENT ? getMobileOperatingSystem(IMPRESSION_USERAGENT) : 'Unknown';

  const publisher =
    IMPRESSION_PUBLISHERNAME && IMPRESSION_PUBLISHERNAME !== 'N/A'
      ? `Published by - ${safeDecode(IMPRESSION_PUBLISHERNAME).replace(/[+_]/g, ' ')}`
      : null;

  let appSiteName = '';
  let appSiteLink = '';
  let applicationPlatform = '';

  if (
    (!IMPRESSION_APPSTORE_ID ||
      IMPRESSION_APPSTORE_ID === 'N/A' ||
      IMPRESSION_APPSTORE_ID === 'null') &&
    (!IMPRESSION_APPNAME || IMPRESSION_APPNAME === 'N/A') &&
    (!IMPRESSION_SITE_ID || IMPRESSION_SITE_ID === 'N/A') &&
    (!IMPRESSION_SITENAME || IMPRESSION_SITENAME === 'N/A')
  ) {
    appSiteName = PAGE_URLHOST ? `Served on ${safeDecode(PAGE_URLHOST)}` : '';
    appSiteLink = IMPRESSION_REFERRER ? safeDecode(IMPRESSION_REFERRER) : '';
    applicationPlatform = PAGE_URLHOST ? determineAdServer(PAGE_URLHOST) : '';
    if (IMPRESSION_REFERRER && applicationPlatform !== 'Application') {
      applicationPlatform = determineAdServer(IMPRESSION_REFERRER);
    }
  } else if (
    (IMPRESSION_APPSTORE_ID || IMPRESSION_APPSTORE_ID !== 'null') &&
    IMPRESSION_APPNAME
  ) {
    const decodedAppName = safeDecode(IMPRESSION_APPNAME)
      .replace(/[+_]/g, ' ')
      .split(':')
      .pop();
    appSiteName = `Served on - ${decodedAppName}`;
    appSiteLink = safeDecode(IMPRESSION_APPSTORE_ID);
    applicationPlatform = 'Application';
  } else if (IMPRESSION_SITENAME && !IMPRESSION_SITE_ID) {
    appSiteName = `Served on - ${safeDecode(IMPRESSION_SITENAME)
      .replace(/[+_]/g, ' ')
      .split(':')
      .pop()}`;
    appSiteLink = IMPRESSION_REFERRER || '';
    applicationPlatform = 'Application';
  } else if (IMPRESSION_SITENAME || IMPRESSION_SITE_ID) {
    const tempSiteName = safeDecode(IMPRESSION_SITENAME || IMPRESSION_SITE_ID)
      .replace(/[+_]/g, ' ')
      .split(':')
      .pop()
      .replace(/^\/+|\/+$/g, '');
    // Develop's distinction: when the site itself is detected as an ad
    // server, prefix with "Served via" (the ad ran THROUGH that server);
    // otherwise "Served on" (ran directly on the site/app).
    appSiteName = `${
      determineAdServer(tempSiteName) === 'Ad Server' ? 'Served via' : 'Served on'
    } - ${tempSiteName}`;
    appSiteLink = IMPRESSION_REFERRER || `https://${tempSiteName.trim()}`;
    applicationPlatform = 'Website';
  }

  return {
    type: 'Impression',
    timestamp: IMPRESSION_TIME,
    title: appSiteName || 'Impression',
    applicationPlatform: applicationPlatform || null,
    subtitle: publisher,
    link: appSiteLink || null,
    platform: OS && OS !== 'Unknown' ? OS : applicationPlatform || OS,
    platformTooltip: IMPRESSION_USERAGENT || null,
    creativeUrl: CREATIVEURL || null,
    eventId: CREATIVEID || `imp-${i}`,
  };
};

const eventTypeLabel = (eventName) => {
  if (!eventName) return 'Event';
  // Map raw analytics event names to readable labels.
  const lower = String(eventName).toLowerCase();
  if (lower === 'page_view') return 'Page View';
  if (lower === 'click') return 'Click';
  if (lower === 'scroll') return 'Scroll';
  if (lower === 'session_start') return 'Session Start';
  if (lower === 'session_end') return 'Session End';
  if (lower === 'submit') return 'Submit';
  // Fallback — title-case underscores.
  return eventName
    .toString()
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
};

const normalizeJourneyEvent = (event, i) => ({
  type: eventTypeLabel(event.EVENT),
  rawType: String(event.EVENT || '').toLowerCase(),
  timestamp: event.DATE,
  title: event.PAGE_URL || '—',
  applicationPlatform: null,
  subtitle: null,
  link: null,
  platform: null,
  platformTooltip: null,
  creativeUrl: null,
  eventId: `evt-${i}`,
});

// ---------------------------------------------------------------------------
// Type → icon + tone (drives the pill color). Tones are CSS classes on the
// pill; the actual hue is set in `_sessions-v1.scss` so we keep design-token
// alignment.
// ---------------------------------------------------------------------------
const TYPE_META = {
  Impression: { Icon: Eye, tone: 'impression' },
  'Page View': { Icon: FileText, tone: 'page-view' },
  Click: { Icon: MousePointerClick, tone: 'click' },
};
const FALLBACK_META = { Icon: Activity, tone: 'event' };

const TypePill = ({ type }) => {
  const meta = TYPE_META[type] || FALLBACK_META;
  const { Icon, tone } = meta;
  return (
    <span
      className={`campaign-sessions-v1__journey-type campaign-sessions-v1__journey-type--${tone}`}
    >
      <Icon size={11} strokeWidth={2} />
      <span>{type}</span>
    </span>
  );
};

const DeviceIcon = ({ device }) => {
  if (device === 'Mobile') return <Smartphone size={13} strokeWidth={1.75} />;
  if (device === 'Tablet') return <Tablet size={13} strokeWidth={1.75} />;
  return <Monitor size={13} strokeWidth={1.75} />;
};

// ---------------------------------------------------------------------------
// Top summary strip — at-a-glance metrics about this session. Mirrors the
// compact-card metric row that PostHog / Hotjar / Plausible all use above
// session detail.
// ---------------------------------------------------------------------------
const SummaryStrip = ({
  pages,
  durationInSeconds,
  device,
  landingPage,
  exitPage,
  sessionStart,
}) => (
  <div className="campaign-sessions-v1__journey-summary">
    {sessionStart && (
      <span className="campaign-sessions-v1__journey-summary-item">
        <Clock size={13} strokeWidth={1.75} />
        <span>{moment(sessionStart).format('MMM DD, YYYY · HH:mm:ss')}</span>
      </span>
    )}
    <span className="campaign-sessions-v1__journey-summary-item">
      <Files size={13} strokeWidth={1.75} />
      <span>
        {pages} {pages === 1 ? 'page' : 'pages'}
      </span>
    </span>
    {Number.isFinite(durationInSeconds) && (
      <span className="campaign-sessions-v1__journey-summary-item">
        <Activity size={13} strokeWidth={1.75} />
        <span>{secondsToTimeText(durationInSeconds)}</span>
      </span>
    )}
    {device && (
      <span className="campaign-sessions-v1__journey-summary-item">
        <DeviceIcon device={device} />
        <span>{device}</span>
      </span>
    )}
    {(landingPage || exitPage) && (
      <span className="campaign-sessions-v1__journey-summary-item campaign-sessions-v1__journey-summary-flow">
        <Tooltip overlayClassName="mjl-ds" title={landingPage || '—'}>
          <span className="campaign-sessions-v1__journey-summary-page">
            {landingPage || '—'}
          </span>
        </Tooltip>
        <ArrowRight size={11} strokeWidth={2} />
        <Tooltip overlayClassName="mjl-ds" title={exitPage || '—'}>
          <span className="campaign-sessions-v1__journey-summary-page">
            {exitPage || '—'}
          </span>
        </Tooltip>
      </span>
    )}
  </div>
);

const SessionJourneyExpand = ({
  impressions = [],
  journey = [],
  sessionStart,
  durationInSeconds,
  landingPage,
  exitPage,
  device,
}) => {
  // Merge the two streams into one chronological list. `record` events are
  // dropped (legacy filter). Sort ascending so users read entry → exit
  // top-to-bottom.
  const events = useMemo(() => {
    const fromImpressions = (impressions || []).map(normalizeImpression);
    const fromJourney = (journey || [])
      .filter((event) => event.EVENT !== 'record')
      .map(normalizeJourneyEvent);
    const merged = [...fromImpressions, ...fromJourney];
    return merged.sort(
      (a, b) => new Date(a.timestamp).valueOf() - new Date(b.timestamp).valueOf()
    );
  }, [impressions, journey]);

  const pageCount = useMemo(
    () =>
      (journey || []).reduce(
        (acc, curr) => (curr.EVENT === 'page_view' ? acc + 1 : acc),
        0
      ),
    [journey]
  );

  const [previewCreative, setPreviewCreative] = useState(null);

  return (
    <div className="campaign-sessions-v1__journey">
      <Modal
        rootClassName="mjl-ds"
        title={previewCreative?.eventId || 'Impression'}
        visible={!!previewCreative}
        footer={null}
        onCancel={() => setPreviewCreative(null)}
      >
        {previewCreative?.creativeUrl && (
          <img
            src={previewCreative.creativeUrl}
            alt={previewCreative.eventId}
            width="100%"
            style={{ borderRadius: 8 }}
          />
        )}
      </Modal>
      <SummaryStrip
        pages={pageCount}
        durationInSeconds={durationInSeconds}
        device={device}
        landingPage={landingPage}
        exitPage={exitPage}
        sessionStart={sessionStart}
      />

      {events.length === 0 ? (
        <div className="campaign-sessions-v1__journey-empty">
          No events recorded for this session.
        </div>
      ) : (
        <ol className="campaign-sessions-v1__journey-list">
          {events.map((event, i) => (
            <li key={event.eventId || i} className="campaign-sessions-v1__journey-row">
              <span className="campaign-sessions-v1__journey-time">
                {event.timestamp
                  ? moment(event.timestamp).format('HH:mm:ss')
                  : '—'}
              </span>

              <span className="campaign-sessions-v1__journey-rail" aria-hidden="true">
                <span className="campaign-sessions-v1__journey-dot" />
              </span>

              {event.type === 'Impression' && (
                <div
                  className={
                    'campaign-sessions-v1__journey-creative' +
                    (event.creativeUrl ? '' : ' campaign-sessions-v1__journey-creative--static')
                  }
                  onClick={() => event.creativeUrl && setPreviewCreative(event)}
                  role={event.creativeUrl ? 'button' : undefined}
                  tabIndex={event.creativeUrl ? 0 : undefined}
                  aria-label={event.creativeUrl ? `Preview impression ${event.eventId || ''}` : undefined}
                  data-no-expand
                >
                  <div
                    className="campaign-overview__creatives-thumb"
                    style={
                      event.creativeUrl
                        ? { backgroundImage: `url("${event.creativeUrl}")` }
                        : undefined
                    }
                  >
                    {!event.creativeUrl && (
                      <ImageIcon size={18} color="#94a3b8" strokeWidth={1.75} />
                    )}
                  </div>
                </div>
              )}

              <div className="campaign-sessions-v1__journey-body">
                <div className="campaign-sessions-v1__journey-head">
                  <TypePill type={event.type} />
                  {event.platform && (
                    <Tooltip overlayClassName="mjl-ds" title={event.platformTooltip || event.platform}>
                      <span className="campaign-sessions-v1__journey-meta">
                        {event.platform}
                      </span>
                    </Tooltip>
                  )}
                </div>
                <Tooltip overlayClassName="mjl-ds" title={event.title}>
                  <div className="campaign-sessions-v1__journey-title">
                    {event.title}
                  </div>
                </Tooltip>
                {event.applicationPlatform && (() => {
                  const Icon = APP_PLATFORM_ICONS[event.applicationPlatform] || Globe;
                  return (
                    <div className="campaign-sessions-v1__journey-app-platform">
                      <Icon size={13} strokeWidth={1.75} style={{ marginRight: 6 }} />
                      {event.applicationPlatform}
                    </div>
                  );
                })()}
                {event.subtitle && (
                  <div className="campaign-sessions-v1__journey-subtitle">
                    {event.subtitle}
                  </div>
                )}
              </div>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
};

export default SessionJourneyExpand;
